import React, { useEffect, useState } from "react";
import { Typography, Button, Grid, Box } from "@mui/material";
import PublicIcon from "@mui/icons-material/Public";
import RegionsSideBarTransferList from "./CategoriesCheckBoxList/RegionsSideBarTransferList";
import useGetAll from "@Hooks/useGetAll";
import { setSnackbar } from "@Redux/snackBarSlice";
import { useDispatch } from "react-redux";
import LoadingComponent from "@Components/LoadingComponent/LoadingComponent";
import SearchableDropDown from "../../../Components/DropDown/SearchableDropDown";
import CountrySelector from "./CountrySelector";
const RegionsSideBar = ({
  selectedCategories,
  setSelectedCategories,
  categories,
  setCategories,
  regions,
  setRegions,
  currentRegion,
  setCurrentRegion,
  targetedMembers,
  selectedCountries = [],
  setSelectedCountries = () => {},
  countryError = "",
  setCountryError = () => {},
  isUiExitRequest = false,
}) => {
  const [isTagEmpty, setIsTagEmpty] = useState(false);
  const [error, setError] = useState(false);
  const [categoriesRegionMap, setCategoriesRegionMap] = useState({});
  const [countriesRegionMap, setCountriesRegionMap] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [allRegionsData, setAllRegionsData] = useState([]); // Store all fetched regions
  const [selectedCountryRegion, setSelectedCountryRegion] = useState("");
  const { getAll } = useGetAll();
  const dispatch = useDispatch();

  const filterCategories = () => {
    const categoryRegionId = isUiExitRequest
      ? allRegionsData.find((region) => region.name === "UI")?.id
      : currentRegion;

    if (categoryRegionId && categoriesRegionMap[categoryRegionId]) {
      const existingRegionCategories = [...categoriesRegionMap[categoryRegionId]];
      setCategories(removeRedundant(existingRegionCategories, categoryRegionId));
    }
    setChecked([]);
    // Note: We don't clear selectedCountries here anymore since it tracks per region
    // Each region maintains its own country selections
    setCountryError("");
  };

  // Keep UI countries grouped by their real region; submit logic flattens them later.
  const handleCountryToggle = (newValue) => {
    const countryRegionId = isUiExitRequest ? selectedCountryRegion : currentRegion;

    setSelectedCountries((prev) => ({
      ...prev,
      [countryRegionId]: newValue,
    }));
    // Clear error when user selects a country
    if (countryError && newValue.length > 0) {
      setCountryError("");
    }
  };

  // Fetch regions and categories from API — runs once on mount
  useEffect(() => {
    setIsLoading(true);

    getAll("/region/allDTO", "Failed to fetch regions")
      .then((data) => {
        if (!data) {
          setError(true);
          return;
        }

        // Store all fetched data for later filtering
        setAllRegionsData(data);

        const regionsMap = {};
        const countriesMap = {};
        const regionsList = [];

        data.forEach((region) => {
          // Create region object
          const regionObj = {
            id: region.id,
            name: region.name,
          };

          regionsList.push(regionObj);

          // Map categories to their respective regions with members
          regionsMap[region.id] = region.categories.map((category) => ({
            id: category.id,
            name: category.name,
            members: category.members || [],
            tag: regionObj,
          }));

          // Map countries to their respective regions
          // Backend now provides countries array in each region
          countriesMap[region.id] = region.countries || [];
        });
        console.log("data", data);
        console.log("regionsMap", regionsMap);
        console.log("countriesMap", countriesMap);
        console.log("regionsList", regionsList);

        // NOTE: selectedCategories restoration from targetedMembers is handled in the
        // separate useEffect below so it runs after edit mode populates targetedMembers.

        setCategoriesRegionMap(regionsMap);
        setCountriesRegionMap(countriesMap);
        setRegions(regionsList);
        setError(false);
      })
      .catch(() => {
        setError(true);
        dispatch(
          setSnackbar({
            open: true,
            message: "Failed to load regions data",
            error: true,
          })
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restore selectedCategories from targetedMembers whenever either value changes.
  // This is split from the fetch effect so it works correctly in edit mode,
  // where targetedMembers arrives asynchronously after the region data.
  useEffect(() => {
    console.log("[RegionsSideBar] restoration effect fired", {
      targetedMembersLength: targetedMembers?.length,
      allRegionsDataLength: allRegionsData?.length,
      targetedMembers,
    });
    if (!targetedMembers || targetedMembers.length === 0) return;
    if (!allRegionsData || allRegionsData.length === 0) return;

    // Rebuild the regionsMap from allRegionsData (same logic as the fetch effect)
    const regionsMap = {};
    allRegionsData.forEach((region) => {
      const regionObj = { id: region.id, name: region.name };
      regionsMap[region.id] = region.categories.map((category) => ({
        id: category.id,
        name: category.name,
        members: category.members || [],
        tag: regionObj,
      }));
    });

    console.log("targetedMembers", targetedMembers);
    setSelectedCategories(
      targetedMembers
        .filter((member) => member.role !== "RD_COORDINATOR") // skip coordinator entries
        .map((member) => {
          const regionCategories = regionsMap[member.region?.id] || [];

          // Prefer categoryAssignments (has descriptions) over plain categories (strings only)
          const categoriesSource =
            member.categoryAssignments && member.categoryAssignments.length > 0
              ? member.categoryAssignments
              : member.categories || [];

          const selectedCategoriesWithMembers = categoriesSource.flatMap((categoryEntry) => {
            const normalizedCategory =
              typeof categoryEntry === "string"
                ? { name: categoryEntry }
                : {
                    id:
                      categoryEntry.categoryId ??
                      categoryEntry.id ??
                      categoryEntry?.category?.id,
                    name:
                      categoryEntry.name ??
                      categoryEntry.categoryName ??
                      categoryEntry?.category?.name,
                    description:
                      categoryEntry.description ??
                      categoryEntry.restrictionDescription ??
                      categoryEntry.categoryDescription ??
                      "",
                  };

            const category = regionCategories.find((cat) =>
              normalizedCategory.id
                ? cat.id === normalizedCategory.id
                : cat.name === normalizedCategory.name
            );

            if (
              category &&
              category.members.some((m) => m.id === member.id)
            ) {
              return {
                id: category.id,
                name: category.name,
                members: category.members,
                tag: member.region || { id: null, name: "N/A" },
                selectedMember: member.id,
                restrictionDescription:
                  normalizedCategory.description || "",
                restrictionDescriptionError: false,
              };
            }
            return [];
          });

          return selectedCategoriesWithMembers;
        })
        .flat()
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetedMembers, allRegionsData]);

  useEffect(() => {
    if (currentRegion) {
      filterCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRegion, selectedCategories]);

  // Re-filter regions when isUiExitRequest changes
  useEffect(() => {
    if (allRegionsData.length === 0) return;

    const UI_REGION_NAME = "UI";
    const regionsMap = {};
    const countriesMap = {};
    const regionsList = [];

    allRegionsData.forEach((region) => {
      const regionObj = {
        id: region.id,
        name: region.name,
      };

      // UI requests use the synthetic UI region for categories, while their
      // countries are selected from the real regions.
      if (isUiExitRequest) {
        if (region.name === UI_REGION_NAME) {
          regionsMap[region.id] = region.categories.map((category) => ({
            id: category.id,
            name: category.name,
            members: category.members || [],
            tag: regionObj,
          }));
        }
      } else {
        if (region.name === UI_REGION_NAME) {
          return;
        }
      }

      if (region.name !== UI_REGION_NAME) {
        regionsList.push(regionObj);
      }

      // Map categories to their respective regions with members
      if (!isUiExitRequest) {
        regionsMap[region.id] = region.categories.map((category) => ({
          id: category.id,
          name: category.name,
          members: category.members || [],
          tag: regionObj,
        }));
      }

      // Map countries to their respective regions
      countriesMap[region.id] = region.countries || [];
    });

    const uiRegion = allRegionsData.find((region) => region.name === UI_REGION_NAME);
    if (isUiExitRequest && uiRegion && currentRegion !== uiRegion.id) {
      setCurrentRegion(uiRegion.id);
    } else if (!isUiExitRequest && currentRegion) {
      // Clear current region selection if it's no longer in the filtered list
      const currentRegionInList = regionsList.find(r => r.id === parseInt(currentRegion));
      if (!currentRegionInList) {
        setCurrentRegion("");
      }
    }

    setCategoriesRegionMap(regionsMap);
    setCountriesRegionMap(countriesMap);
    setRegions(regionsList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUiExitRequest, allRegionsData]);

  // Modified to consider both category ID AND region ID when filtering
  const removeRedundant = (categories, regionId) => {
    return categories.filter((category) => {
      // Find if this category is already selected from this specific region
      return !selectedCategories.some(
        (selectedCategory) =>
          selectedCategory.id === category.id &&
          selectedCategory.tag.id === parseInt(regionId)
      );
    });
  };

  const [checked, setChecked] = useState([]);
  const handleToggle = (value) => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }
    setChecked(newChecked);
  };

  // Handle Select All - sets all items in a single state update
  const handleSelectAll = (items) => {
    setChecked(items);
  };

  const addToSelected = () => {
    const uiRegionId = allRegionsData.find((region) => region.name === "UI")?.id;
    const categoryRegionId = isUiExitRequest ? uiRegionId : currentRegion;
    const countryRegionId = isUiExitRequest ? selectedCountryRegion : currentRegion;
    const availableCountries = countriesRegionMap[countryRegionId] || [];
    const currentRegionCountries = isUiExitRequest
      ? Object.values(selectedCountries).flat()
      : selectedCountries[currentRegion] || [];

    if (isUiExitRequest && !selectedCountryRegion) {
      setCountryError("Please select a country region first.");
      dispatch(
        setSnackbar({
          open: true,
          message: "Please select a country region first.",
          error: true,
        })
      );
      return;
    }

    // Only validate country selection if countries are available for this region
    if (currentRegion && availableCountries.length > 0 && currentRegionCountries.length === 0) {
      setCountryError("Please select at least one country before adding categories.");
      dispatch(
        setSnackbar({
          open: true,
          message: "Please select at least one country before adding categories.",
          error: true,
        })
      );
      return;
    }

    const regionId = parseInt(categoryRegionId, 10);
    const regionName = allRegionsData.find((r) => r.id === regionId)?.name;

    // Make sure each selected category maintains information about its region
    const categoriesToAdd = checked.map((category) => ({
      ...category,
      tag:
        category.tag || {
          id: regionId,
          name: regionName,
        },
      restrictionDescription: category.restrictionDescription || "",
      restrictionDescriptionError: false,
    }));

    setSelectedCategories([...selectedCategories, ...categoriesToAdd]);

    // Only remove the selected categories from the current region's list
    setCategories(categories.filter((value) => checked.indexOf(value) === -1));
    setChecked([]);
  };

  // Show loading indicator while fetching regions
  if (isLoading) {
    return (
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        sx={{ height: "100%" }}
      >
        <LoadingComponent size="3em" thickness={4} color="primary" />
      </Grid>
    );
  }

  return (
    <Grid container rowSpacing={1}>
      <Grid item xs={12}>
        <Box alignItems="center" display="flex" flexDirection="row">
          <Box className="regionsSideBarIconBox">
            <PublicIcon className="icon" fontSize="medium" />
          </Box>
          <Typography
            variant="h6"
            fontWeight="bold"
            className="regionsSideBarHeader"
          >
            Regions & Categories
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <SearchableDropDown
          state={isUiExitRequest ? selectedCountryRegion : currentRegion}
          isEmpty={isTagEmpty}
          setIsEmpty={setIsTagEmpty}
          setState={isUiExitRequest ? setSelectedCountryRegion : setCurrentRegion}
          placeholder={isUiExitRequest ? "Select a country region" : "Select a Region"}
          size="small"
          items={regions}
          name={isUiExitRequest ? "Country region" : ""}
          defaultFirst={!isUiExitRequest}
        />
      </Grid>

      {/* Country Selector - Shows when region is selected */}
      {((isUiExitRequest && selectedCountryRegion) || (!isUiExitRequest && currentRegion)) && (
        <Grid item xs={12} sx={{ mt: 2 }}>
          {/* Uses countries from backend response */}
          <CountrySelector
            regionName={regions.find(r => r.id === parseInt(isUiExitRequest ? selectedCountryRegion : currentRegion))?.name || ''}
            countries={countriesRegionMap[isUiExitRequest ? selectedCountryRegion : currentRegion] || []}
            selectedCountries={
              selectedCountries[isUiExitRequest ? selectedCountryRegion : currentRegion] || []
            }
            onCountryToggle={handleCountryToggle}
            error={countryError}
          />
        </Grid>
      )}

      {error && (
        <Typography variant="body1" color="error" sx={{ alignSelf: "center" }}>
          Error loading Regions Data
        </Typography>
      )}
      {!error &&
        categoriesRegionMap[isUiExitRequest ? allRegionsData.find((region) => region.name === "UI")?.id : currentRegion] &&
        categoriesRegionMap[isUiExitRequest ? allRegionsData.find((region) => region.name === "UI")?.id : currentRegion].length !== 0 && (
          <Grid item xs={12}>
            <RegionsSideBarTransferList
              items={categories}
              handleToggle={handleToggle}
              checked={checked}
              onSelectAll={handleSelectAll}
            />
            <Grid>
              <Box className="submissionBox">
                <Button
                  className="submissionButton"
                  variant="outlined"
                  color="primary"
                  size="large"
                  onClick={() => setChecked([])}
                  disabled={checked.length === 0}
                >
                  Cancel
                </Button>
                <Button
                  className="submissionButton"
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={addToSelected}
                  disabled={checked.length === 0}
                >
                  Add
                </Button>
              </Box>
            </Grid>
          </Grid>
        )}
    </Grid>
  );
};

export default RegionsSideBar;
